"use client";

import { useGetAds } from "@/hooks/main-hook";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, EffectFade, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";

const defaultAds = [
  {
    id: "default-hero-2",
    type: "image",
    media:
      "/AQPjl3Wkff0ZVJGzNw6qN54RipQI9DvXPc52HOifF0elQN-mc2XSjoKfBkuT_9OGA1XJG6uSSCkOccIAA2BiNp5ZYM3L-ITcxdNpidFeUbpFP1LGy62pc0sjB2N2_NpPWw4VNahBqkSaZzT7LwJg2CEC1JrG.jpeg",
    title: {
      ar: "SNA العقارية – نافذتك لعالم العقار",
      en: "SNA Real Estate – Your Gateway to Property",
    },
    subTitle: {
      ar: "اكتشف فرص البيع والتأجير في أفضل المواقع بالمملكة",
      en: "Discover the best buying and rental opportunities across Saudi Arabia",
    },
    description: {
      ar: "نوفر لك تجربة بحث ذكية تجمع بين التراث السعودي والحداثة لتصل إلى عقارك المثالي بكل ثقة.",
      en: "We offer a smart property search experience that blends Saudi heritage with modern living — helping you find your perfect home with confidence.",
    },
  },
  {
    id: "default-hero",
    type: "image",
    media: "/banner.jpeg",
    title: {
      ar: "تجربة ثلاثية الأبعاد للعقارات",
      en: "Immersive 3D Property Tours",
    },
    subTitle: {
      ar: "تجول داخل عقارك قبل الشراء",
      en: "Walk Through Your Property Before Buying",
    },
    description: {
      ar: "استكشف العقارات بدقة ثلاثية الأبعاد من أي مكان.",
      en: "Explore properties in realistic 3D from anywhere.",
    },
    link: "/properties",
  },
];

export default function Hero() {
  const t = useTranslations("Home");
  const locale = useLocale();
  const { data, isSuccess } = useGetAds({ page: 1, limit: 30 });
  const queryClient = useQueryClient();

  const ads = useMemo(() => {
    if (!isSuccess || !data?.ads?.length) return defaultAds;

    const filtered = data.ads.filter(
      (ad: any) => !defaultAds.some((d) => d.id === ad.id),
    );

    return [...defaultAds, ...filtered];
  }, [isSuccess, data]);

  useEffect(() => {
    const eventSource = new EventSource(
      `${process.env.NEXT_PUBLIC_API_URI}/api/v1/notifications/ads`,
    );

    eventSource.onmessage = async (event) => {
      const payload = JSON.parse(event.data);
      if (payload.type === "ADS_FETCHED") {
        queryClient.invalidateQueries({ queryKey: ["ads"] });
      }
    };

    return () => eventSource.close();
  }, [queryClient]);

  return (
    <section className="relative w-full h-[85vh] min-h-130 overflow-hidden bg-black">
      {/* Custom Arrows */}

      {defaultAds?.length >= 1 && (
        <>
          <div className="absolute z-20 inset-y-0 left-4 flex items-center">
            <button
              className="hero-prev group w-12 h-12 cursor-pointer rounded-full bg-white/10 backdrop-blur border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center"
              aria-label="Previous slide"
            >
              <ChevronLeft className="text-white group-hover:scale-110 transition" />
            </button>
          </div>

          <div className="absolute z-20 inset-y-0 right-4 flex items-center">
            <button
              className="hero-next group w-12 h-12 cursor-pointer rounded-full bg-white/10 backdrop-blur border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center"
              aria-label="Next slide"
            >
              <ChevronRight className="text-white group-hover:scale-110 transition" />
            </button>
          </div>
        </>
      )}

      <Swiper
        modules={[Navigation, Autoplay, EffectFade, Pagination]}
        effect="fade"
        loop
        autoplay={{
          delay: 6000,
          disableOnInteraction: false,
        }}
        navigation={{
          nextEl: ".hero-next",
          prevEl: ".hero-prev",
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        speed={1200}
        className="w-full h-full"
      >
        {ads.map((ad: any) => (
          <SwiperSlide key={ad.id}>
            <div className="relative w-full h-full flex items-center">
              {/* Background */}
              {ad.type === "video" ? (
                <video
                  src={ad.media}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <Image
                  src={ad.media}
                  alt="Hero background"
                  fill
                  priority
                  className="object-cover object-center"
                />
              )}

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-linear-to-b from-black/80 via-black/60 to-black/90" />

              {/* Content */}
              <div className="relative z-10 container max-w-[85%] mx-auto px-6 lg:px-10 grid grid-cols-1 gap-12 items-center">
                <div className="space-y-6 max-w-2xl ltr:max-w-5xl animate-fade-in">
                  <h1 className="text-3xl sm:text-5xl  font-extrabold leading-tight tracking-tight text-white drop-shadow">
                    {locale === "ar" ? ad.title?.ar : ad.title?.en}
                  </h1>

                  {ad.subTitle?.[locale] && (
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-medium text-white/90">
                      {ad.subTitle[locale]}
                    </h2>
                  )}

                  {ad.description?.[locale] && (
                    <p className="text-base sm:text-lg lg:text-xl text-white/80 leading-relaxed max-w-xl">
                      {ad.description?.[locale]}
                    </p>
                  )}

                  {ad.link && (
                    <div className="pt-4">
                      <Link
                        href={ad.link}
                        className="inline-flex items-center gap-3 bg-main hover:bg-main-dark text-white font-semibold text-base sm:text-lg px-7 py-3.5 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 border border-main/60"
                      >
                        {t("exploreButton")}
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                    </div>
                  )}
                </div>

                {/* Decorative Right Side (Optional visual layer) */}
                <div className="hidden lg:block relative h-h-105 w-full">
                  <div className="absolute inset-0 rounded-3xl bg-white/5 backdrop-blur-md border border-white/10 shadow-2xl" />
                  <div className="absolute inset-6 rounded-2xl overflow-hidden">
                    {ad.type !== "video" && (
                      <Image
                        src={ad.media}
                        alt="Preview"
                        fill
                        className="object-cover scale-105"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}
